import { useEffect, useState } from "react";
import { Loader2, Save, ShieldCheck, KeyRound, Trash2, Info } from "lucide-react";
import { toast } from "sonner";
import OwnerLayout from "@/components/owner/OwnerLayout";
import apiClient, { formatApiErrorDetail } from "@/lib/api";

export default function OwnerPaymentSettingsPage() {
  const [config, setConfig] = useState(null);
  const [merchantCode, setMerchantCode] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [env, setEnv] = useState("sandbox");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () =>
    apiClient.get("/owner/payment-settings")
      .then((r) => { setConfig(r.data); if (r.data?.env) setEnv(r.data.env); })
      .catch(() => {})
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!merchantCode.trim() || !apiKey.trim()) {
      toast.error("Merchant Code dan API Key wajib diisi.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await apiClient.put("/owner/payment-settings", {
        merchant_code: merchantCode.trim(),
        api_key: apiKey.trim(),
        env,
      });
      setConfig(data);
      setMerchantCode("");
      setApiKey("");
      toast.success(`Kredensial Duitku (${env}) berhasil disimpan dengan aman.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Hapus kredensial Duitku? Properti Anda akan menampilkan 'belum menyediakan payment gateway' sampai diisi kembali.")) return;
    try {
      const { data } = await apiClient.delete("/owner/payment-settings");
      setConfig(data);
      setEnv("sandbox");
      toast.success("Kredensial dihapus.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  return (
    <OwnerLayout testid="owner-payment-settings-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight mb-1">Payment Settings</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Gunakan akun Duitku Anda sendiri agar pembayaran dari properti Anda masuk langsung ke akun Anda.
      </p>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-primary" size={26} /></div>
      ) : (
        <div className="space-y-6 max-w-2xl">
          {/* Current status */}
          <div className="bg-card border border-border rounded-2xl p-6" data-testid="payment-status-card">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={18} className={config?.configured ? "text-emerald-500" : "text-muted-foreground"} />
              <h2 className="font-heading text-lg font-semibold">Status Duitku</h2>
            </div>
            {config?.configured ? (
              <div className="space-y-1.5 text-sm" data-testid="payment-configured">
                <p>Status: <span className="font-semibold text-emerald-600">Terkonfigurasi</span></p>
                <p>Merchant Code: <span className="font-mono font-medium">{config.merchant_code}</span></p>
                <p>API Key: <span className="font-mono">••••••••{config.api_key_last4}</span></p>
                <p>Mode: <span className="font-medium capitalize">{config.env}</span></p>
                <button onClick={clear} data-testid="payment-clear"
                  className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-rose-200 text-rose-600 text-sm font-semibold hover:bg-rose-50 transition-colors">
                  <Trash2 size={15} /> Hapus Kredensial
                </button>
              </div>
            ) : (
              <div className="flex items-start gap-2 text-sm text-muted-foreground" data-testid="payment-not-configured">
                <Info size={16} className="shrink-0 mt-0.5" />
                <p>Belum dikonfigurasi. Selama belum diisi, halaman pembayaran properti Anda akan menampilkan <span className="italic">"Maaf, kos ini belum menyediakan payment gateway"</span>.</p>
              </div>
            )}
          </div>

          {/* Form */}
          <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
            <div className="flex items-center gap-2">
              <KeyRound size={18} className="text-primary" />
              <h2 className="font-heading text-lg font-semibold">
                {config?.configured ? "Perbarui Kredensial" : "Masukkan Kredensial Duitku"}
              </h2>
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1.5">Mode / Environment</label>
              <div className="flex gap-2" data-testid="payment-env-toggle">
                {[
                  { v: "sandbox", label: "Sandbox (Uji Coba)" },
                  { v: "production", label: "Production (Live)" },
                ].map((opt) => (
                  <button key={opt.v} type="button" onClick={() => setEnv(opt.v)}
                    data-testid={`payment-env-${opt.v}`}
                    className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${env === opt.v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary/60"}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Pilih <span className="font-medium">Sandbox</span> untuk uji coba, atau <span className="font-medium">Production</span> saat sudah siap menerima pembayaran asli. Sistem otomatis memakai server Duitku yang sesuai.
              </p>
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Merchant Code</label>
              <input value={merchantCode} onChange={(e) => setMerchantCode(e.target.value)} data-testid="payment-merchant-code"
                placeholder="Contoh: DXXXX" autoComplete="off"
                className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">API Key (Merchant Key)</label>
              <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} data-testid="payment-api-key"
                placeholder="Masukkan API Key Duitku" autoComplete="new-password"
                className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
              <p className="text-xs text-muted-foreground mt-1">
                API Key disimpan terenkripsi dan tidak pernah ditampilkan kembali secara utuh.
              </p>
            </div>
            <button onClick={save} disabled={saving} data-testid="payment-save"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Simpan Kredensial
            </button>
          </div>
        </div>
      )}
    </OwnerLayout>
  );
}
